(() => {
  if (customElements.get('mloong-fps')) return;
  const resolutions = ['1080p', '1440p', '4k'];
  const storageKey = 'mloong-fps-resolution';
  const instances = new Set();
  let active = null;
  let sequence = 0;
  let preferred = null;
  try { preferred = sessionStorage.getItem(storageKey); } catch (_) {}
  const save = (resolution) => {
    preferred = resolution;
    try { sessionStorage.setItem(storageKey, resolution); } catch (_) {}
    instances.forEach((instance) => instance.select(resolution));
  };
  class MloongFPS extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.panel = this.querySelector('[data-fps-panel]');
      if (!this.panel) return;
      this.controller = new AbortController();
      this.signal = this.controller.signal;
      instances.add(this);
      this.available = this.dataset.available.split(',').filter((r) => resolutions.includes(r));
      this.summary = this.querySelector('.m-fps__trigger');
      this.disclosure = this.querySelector('.m-fps__disclosure');
      this.panel.addEventListener('click', (event) => {
        const button = event.target.closest('[data-fps-resolution]');
        if (button && !button.disabled) save(button.dataset.fpsResolution);
        if (event.target.closest('[data-fps-close]')) this.close(true);
      }, { signal: this.signal });
      this.select(preferred || this.dataset.default, false);
      this.panel.querySelectorAll('img').forEach((img) => {
        const hide = () => { img.hidden = true; };
        img.addEventListener('error', hide, { signal: this.signal });
        if (img.complete && !img.naturalWidth) hide();
      });
      if (!this.summary || typeof HTMLDialogElement === 'undefined') return;
      this.summary.setAttribute('aria-haspopup', 'dialog');
      this.summary.setAttribute('aria-expanded', 'false');
      this.summary.addEventListener('click', (event) => {
        event.preventDefault();
        if (active === this && this.pinned) this.close(true);
        else this.open(true);
      }, { signal: this.signal });
      this.summary.addEventListener('pointerenter', (event) => {
        if (this.suppressHover || event.pointerType !== 'mouse' || !matchMedia('(hover: hover)').matches || matchMedia('(max-width: 749px)').matches) return;
        clearTimeout(this.closeTimer);
        this.openTimer = setTimeout(() => this.open(false), 180);
      }, { signal: this.signal });
      this.summary.addEventListener('pointerleave', () => { this.suppressHover = false; this.scheduleClose(); }, { signal: this.signal });
    }
    select(requested, announce = true) {
      const selected = this.available.includes(requested) ? requested : this.available.includes(this.dataset.default) ? this.dataset.default : this.available[0];
      if (!selected) return;
      const changed = selected !== this.selected;
      this.selected = selected;
      this.panel.querySelectorAll('[data-fps-resolution]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.fpsResolution === selected));
      });
      this.panel.querySelectorAll('[data-fps-game]').forEach((game) => {
        const raw = Number(game.getAttribute(`data-${selected}`));
        const valid = Number.isFinite(raw) && raw > 0;
        game.querySelector('[data-fps-value]').textContent = valid ? (this.dataset.estimated === 'true' ? (raw < 5 ? '<5' : String(Math.floor(raw / 5) * 5) + '+') : String(Math.round(raw))) : this.dataset.untested;
        game.querySelector('[data-fps-unit]').hidden = !valid;
        game.querySelector('.m-fps__value').classList.toggle('is-empty', !valid);
      });
      if (changed && announce) this.panel.querySelector('.m-fps__status').textContent = `${selected === '4k' ? '4K' : selected}: ${this.dataset.announcement}`;
      if (active === this) this.position();
    }
    scheduleClose() {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => {
        if (!this.pinned && !this.dialog?.contains(document.activeElement)) this.close(false);
      }, 250);
    }
    open(pinned) {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      if (!this.isConnected) return;
      if (active === this) {
        if (pinned) {
          this.pinned = true;
          this.panel.querySelector('[data-fps-close]').focus({ preventScroll: true });
        }
        return;
      }
      if (active) active.close(false);
      const dialog = document.createElement('dialog');
      this.dialog = dialog;
      dialog.className = 'm-fps m-fps-dialog';
      dialog.id = `mloong-fps-dialog-${++sequence}`;
      const heading = this.panel.querySelector('.m-fps__heading');
      heading.id = `${dialog.id}-heading`;
      dialog.setAttribute('aria-labelledby', heading.id);
      const product = this.panel.querySelector('.m-fps__product');
      if (product) {
        product.id = `${dialog.id}-product`;
        dialog.setAttribute('aria-describedby', product.id);
      }
      this.summary.setAttribute('aria-controls', dialog.id);
      this.summary.setAttribute('aria-expanded', 'true');
      this.disclosure.open = true;
      dialog.append(this.panel);
      document.body.append(dialog);
      this.pinned = pinned;
      active = this;
      this.modal = matchMedia('(max-width: 749px)').matches;
      this.scrollBeforeOpen = { left: window.scrollX, top: window.scrollY };
      const beforeFocus = document.activeElement;
      if (this.modal) {
        dialog.showModal();
        document.documentElement.classList.add('m-fps-lock');
      } else {
        dialog.show();
      }
      this.position();
      if (pinned || this.modal) this.panel.querySelector('[data-fps-close]').focus({ preventScroll: true });
      else {
        if (dialog.contains(document.activeElement)) document.activeElement.blur();
        if (beforeFocus instanceof HTMLElement && beforeFocus !== document.body) beforeFocus.focus({ preventScroll: true });
      }
      this.openController = new AbortController();
      const signal = this.openController.signal;
      dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.close(true); }, { signal });
      dialog.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); this.close(true); }
        if (event.key === 'Tab' && this.modal) {
          const focusable = [...dialog.querySelectorAll('button:not([disabled]),a[href],summary,[tabindex]:not([tabindex="-1"])')].filter((node) => node.getClientRects().length > 0);
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
        }
      }, { signal });
      dialog.addEventListener('pointerenter', () => clearTimeout(this.closeTimer), { signal });
      dialog.addEventListener('pointerleave', () => this.scheduleClose(), { signal });
      dialog.addEventListener('focusin', () => { this.pinned = true; }, { signal });
      document.addEventListener('pointerdown', (event) => {
        if (event.target === dialog) {
          const rect = dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close(true);
        } else if (!dialog.contains(event.target) && !this.contains(event.target)) this.close(false);
      }, { signal, capture: true });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && active === this) { event.preventDefault(); this.close(true); }
      }, { signal });
      window.addEventListener('resize', () => {
        if (this.modal !== matchMedia('(max-width: 749px)').matches) this.close(true);
        else this.position();
      }, { signal });
      window.addEventListener('scroll', () => this.position(), { signal, capture: true, passive: true });
      this.observer = new MutationObserver(() => {
        const other = [...document.querySelectorAll('dialog[open], [aria-modal="true"]')].find((node) => node !== dialog && node.getClientRects().length && getComputedStyle(node).visibility !== 'hidden');
        if (other) this.close(false);
      });
      this.observer.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open', 'aria-modal', 'class'] });
    }
    position() {
      if (!this.dialog || this.modal) return;
      const rect = this.summary.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) { this.close(false); return; }
      const width = this.dialog.offsetWidth;
      const height = this.dialog.offsetHeight;
      const left = Math.max(12, Math.min(rect.left, window.innerWidth - width - 12));
      const below = rect.bottom + 8;
      const top = below + height <= window.innerHeight - 12 ? below : Math.max(12, rect.top - height - 8);
      this.dialog.style.left = `${left}px`;
      this.dialog.style.top = `${top}px`;
    }
    close(restoreFocus) {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      if (active !== this) return;
      this.suppressHover = true;
      const hadFocus = this.dialog?.contains(document.activeElement);
      this.openController?.abort();
      this.observer?.disconnect();
      this.dialog.close();
      this.disclosure.append(this.panel);
      this.dialog.remove();
      this.dialog = null;
      this.disclosure.open = false;
      this.summary.setAttribute('aria-expanded', 'false');
      this.summary.removeAttribute('aria-controls');
      document.documentElement.classList.remove('m-fps-lock');
      if (this.modal && this.scrollBeforeOpen) window.scrollTo({ ...this.scrollBeforeOpen, behavior: 'instant' });
      active = null;
      this.pinned = false;
      if ((restoreFocus || hadFocus) && this.isConnected) this.summary.focus({ preventScroll: true });
    }
    disconnectedCallback() {
      this.close(false);
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      this.controller?.abort();
      this.controller = null;
      instances.delete(this);
    }
  }
  customElements.define('mloong-fps', MloongFPS);
})();
